import { Box, Button, FormControl, NativeSelect, OutlinedInput, TextField } from "@mui/material";
import React, { useState } from "react";
import { AdminRole, CreateAdminPayload, SurgoAdmin } from "../../../api/surgo";
import { formatRoleTitle } from "../../../utils/adminSession";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// The API requires 3 to 100 characters for each name.
const isValidName = (name: string) => name.trim().length >= 3 && name.trim().length <= 100;

const selectSx = { borderRadius: "8px" };

interface IProps {
  open: boolean;
  /** The admin being edited; omitted when creating one. */
  admin?: SurgoAdmin;
  roles: AdminRole[];
  defaultRoleId?: number;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateAdminPayload) => void;
}

/**
 * Creates an admin, or edits one. Editing only changes the role for now, so the
 * other fields are shown read-only.
 */
const AdminModal: React.FC<IProps> = ({
  open,
  admin,
  roles,
  defaultRoleId,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const isEdit = !!admin;
  const [form, setForm] = useState<CreateAdminPayload>({
    first_name: admin?.first_name ?? "",
    last_name: admin?.last_name ?? "",
    email: admin?.email ?? "",
    role_id: admin?.role_id ?? defaultRoleId ?? 0,
  });

  const handleChange = (key: keyof CreateAdminPayload, value: string) => {
    setForm((prev) => ({ ...prev, [key]: key === "role_id" ? Number(value) : value }));
  };

  const canSubmit = isEdit
    ? form.role_id > 0 && form.role_id !== admin?.role_id
    : isValidName(form.first_name) &&
      isValidName(form.last_name) &&
      isValidEmail(form.email) &&
      form.role_id > 0;

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit admin" : "Add admin"}
      actions={
        <>
          <Button variant="outlined" onClick={onClose} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSubmit({ ...form, email: form.email.trim() })}
            disabled={!canSubmit || isSubmitting}
            sx={modalSubmitButtonSx}
          >
            {isEdit ? "Save" : "Create admin"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
        <TextField
          label="First name *"
          fullWidth
          value={form.first_name}
          disabled={isEdit}
          onChange={(e) => handleChange("first_name", e.target.value)}
          inputProps={{ maxLength: 100 }}
          error={!isEdit && !!form.first_name && !isValidName(form.first_name)}
          helperText={!isEdit && form.first_name && !isValidName(form.first_name) ? "At least 3 characters" : ""}
        />
        <TextField
          label="Last name *"
          fullWidth
          value={form.last_name}
          disabled={isEdit}
          onChange={(e) => handleChange("last_name", e.target.value)}
          inputProps={{ maxLength: 100 }}
          error={!isEdit && !!form.last_name && !isValidName(form.last_name)}
          helperText={!isEdit && form.last_name && !isValidName(form.last_name) ? "At least 3 characters" : ""}
        />
        <TextField
          label="Email *"
          fullWidth
          type="email"
          value={form.email}
          disabled={isEdit}
          onChange={(e) => handleChange("email", e.target.value)}
          inputProps={{ maxLength: 140 }}
          error={!isEdit && !!form.email && !isValidEmail(form.email)}
          helperText={!isEdit && form.email && !isValidEmail(form.email) ? "Enter a valid email address" : ""}
        />
        <FormControl fullWidth>
          <NativeSelect
            value={form.role_id}
            input={<OutlinedInput sx={selectSx} />}
            inputProps={{ "aria-label": "Role" }}
            onChange={(e) => handleChange("role_id", e.target.value)}
          >
            <option value={0}>Select a role *</option>
            {roles.map((role) => (
              <option value={role.id} key={role.id}>
                {formatRoleTitle(role.title)}
              </option>
            ))}
          </NativeSelect>
        </FormControl>
      </Box>
    </ModalWrapper>
  );
};

export default AdminModal;
